import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import EditorialMarquee from "@/components/EditorialMarquee";
import { CITIES, SERVICES } from "@/lib/data";

const CASE_STUDIES = [
  {
    id: "01",
    title: "Bathurst Mining Camp",
    location: "Bathurst · Belledune Corridor",
    citySlug: "bathurst",
    image:
      "https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    stat: { n: "24/7", label: "Perimeter Coverage" },
    challenge:
      "One of the world's most productive zinc-lead-silver camps, active since 1952. Legacy tailings, active exploration and re-processing operations, remote pit locations connected by unmonitored haul roads through Crown land — magnetic to fuel theft, artifact scavenging and eco-protest actions.",
    approach:
      "Two-officer overnight gatehouse posts at the primary shaft compound with weigh-scale integration, hourly mobile patrol sweeps of tailings ponds, and drone-supported perimeter checks of the historic Brunswick #12 exploration corridor. Bilingual officers coordinate with RCMP J Division and DNR conservation on cross-jurisdiction incidents.",
    outcome: [
      "Fuel & copper losses reduced 71% inside first 90 days",
      "Zero recordable eco-protest breaches over 22-month contract",
      "Full incident audit-trail delivered to insurer for renewal",
    ],
  },
  {
    id: "02",
    title: "Sussex-Penobsquis Potash",
    location: "Sussex · Kings County",
    citySlug: "sussex",
    image:
      "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85",
    stat: { n: "1,200m", label: "Underground Access" },
    challenge:
      "Deep-shaft potash operation in the geologically active Penobsquis basin — cavern subsidence risk, hostile terminations from a unionized workforce, and a public rail-loading facility co-located with a residential community. Compliance to WorkSafeNB Underground Mine Regulation 96-105 is non-negotiable.",
    approach:
      "Terminated-employee escort teams, refuge-station drills alongside the mine rescue crew, rail-siding trespass patrol during load-out shifts, and a plainclothes internal-investigations officer working the day-shift processing plant to close a suspected time-fraud ring.",
    outcome: [
      "Recovered $340K annualized against contractor time-fraud",
      "16 successful hostile-termination escorts, zero incidents",
      "WorkSafeNB audit passed with zero findings for 3 consecutive years",
    ],
  },
  {
    id: "03",
    title: "Port of Belledune Critical Minerals",
    location: "Belledune · Chaleur",
    citySlug: "belledune",
    image:
      "https://images.unsplash.com/photo-1617897711385-df9c86b7dfe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBjY3R2JTIwZGFya3xlbnwwfHx8fDE3ODQ5MjM4MzR8MA&ixlib=rb-4.1.0&q=85",
    stat: { n: "$4.2B", label: "Cargo Value Protected" },
    challenge:
      "The Port of Belledune is Atlantic Canada's designated critical-minerals gateway. Copper concentrate, antimony ore and rare-earth intermediate cargoes stage on the wharf for 12–72 hours before vessel loading. Small volumes carry disproportionate value; chain-of-custody documentation must survive marine-insurance and export-control scrutiny.",
    approach:
      "Static wharf guarding on a two-officer 24/7 rotation, CCTV analytics on every stack and a plainclothes investigator embedded during high-value load windows. Bilingual officers integrate with Canada Border Services Agency for outbound container-seal verification.",
    outcome: [
      "Zero cargo shortage claims across 340+ vessel calls",
      "CBSA seal-verification integration adopted as a port standard",
      "Full evidence package ready for every export-control audit",
    ],
  },
];

const CAPABILITIES = [
  "Gatehouse Access Control & Weigh-Scale Integration",
  "Magazine (Explosives) & Cyanide-Shed Patrol",
  "Ore Concentrate & Haul-Truck Seal Verification",
  "Underground Refuge-Station Coordination",
  "Exploration Camp Overnight Guarding (FIFO Ready)",
  "Tailings Pond Perimeter Sweeps",
  "Anti-Picket, Eco-Protest & Anti-Poaching Response",
  "Plainclothes Internal Theft & Time-Fraud Investigations",
  "Bilingual (EN / FR) Northern & Acadian Rosters",
  "24/7/365 Dispatch & Written Post Orders",
];

export default function MiningSector() {
  return (
    <div data-testid="page-sector-mining">
      <Seo
        title="Mining Security Services in NB — Bathurst, Sussex, Belledune | Maze Security"
        description="Specialized mining security across New Brunswick: Bathurst Mining Camp, Sussex potash, Belledune critical minerals. WorkSafeNB Underground Mine Reg 96-105 trained officers."
        path="/sectors/mining"
      />
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-border overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.pexels.com/photos/10292870/pexels-photo-10292870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Mining site security"
            className="w-full h-full object-cover grayscale contrast-125"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        </div>

        <div className="relative mx-auto max-w-[1600px] px-6 md:px-12">
          <Link
            to="/services"
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground hover:text-primary transition-colors mb-8 inline-block"
          >
            ← All Sectors
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
                [ Sector · Mining & Extractive Industries ]
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
                className="font-serif font-light text-5xl md:text-9xl leading-[0.9] tracking-tighter"
              >
                Mining security <br />
                <i className="text-primary">in New Brunswick.</i>
              </motion.h1>
            </div>
            <div className="md:col-span-4">
              <p className="text-foreground/85 leading-relaxed">
                Twenty years securing New Brunswick's most demanding
                extractive-industry sites — from the world-class Bathurst
                Mining Camp to Sussex potash and the critical-minerals
                gateway at Belledune.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro long-form */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-32">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
                [ Sector Doctrine ]
              </div>
              <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tighter leading-[1]">
                Ore. Camp. <br />
                <i className="text-primary">Perimeter.</i>
              </h2>
            </div>
          </div>
          <div className="md:col-span-8 space-y-8">
            <p className="font-serif italic text-2xl md:text-3xl leading-snug text-foreground/90 max-w-2xl">
              A mine is not a warehouse. Its threat model is unique: valuable
              ore in transit, magazines full of explosives, camps full of
              rotating crews, and a public that cares intensely about what
              happens on the surface above.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-foreground/80 max-w-2xl">
              We built our mining division on a single premise — that
              extractive-industry security demands operators who speak fluent
              WorkSafeNB Underground Mine Regulation 96-105, understand the
              MSHA-equivalent standards imported by cross-border operators,
              and treat every officer as an embedded part of the mine team,
              not an outside contractor.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-foreground/80 max-w-2xl">
              Below are three field-tested engagements across New Brunswick's
              flagship mining regions. Each one is an anonymized composite of
              real client work — but the numbers, methods and outcomes are
              exactly the ones we deliver.
            </p>
          </div>
        </div>
      </section>

      {/* Case studies */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ Field Reports ]
          </div>
          <h2 className="font-serif font-light text-4xl md:text-7xl leading-[0.95] tracking-tighter mb-16 max-w-4xl">
            Three Sites. <br />
            <i className="text-primary">One Standard.</i>
          </h2>

          <div className="space-y-24">
            {CASE_STUDIES.map((c, i) => (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
                data-testid={`case-study-${c.citySlug}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12"
              >
                <div className="md:col-span-5">
                  <div className="relative aspect-[4/5] overflow-hidden border border-border sticky top-32">
                    <img
                      src={c.image}
                      alt={c.title}
                      className="absolute inset-0 w-full h-full object-cover grayscale contrast-125"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
                    <div className="absolute top-6 left-6 font-mono text-[10px] tracking-[0.3em] uppercase text-primary">
                      Case · {c.id}
                    </div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/70 flex items-center gap-2 mb-3">
                        <MapPin size={12} /> {c.location}
                      </div>
                      <h3 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight">
                        {c.title}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-7 space-y-8">
                  <div className="border border-border p-6 flex items-end justify-between bg-[hsl(0_0%_6%)]">
                    <div className="font-serif font-light leading-none" style={{ fontSize: "clamp(3rem, 6vw, 5rem)" }}>
                      {c.stat.n}
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground text-right max-w-[45%]">
                      {c.stat.label}
                    </div>
                  </div>

                  <Chapter label="The Challenge" body={c.challenge} />
                  <Chapter label="Our Approach" body={c.approach} />

                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                      [ Outcome ]
                    </div>
                    <ul className="space-y-3">
                      {c.outcome.map((o, k) => (
                        <li key={k} className="flex items-start gap-3 border-b border-border pb-3">
                          <span className="font-mono text-[10px] tracking-widest text-primary pt-1.5">
                            {String(k + 1).padStart(2, "0")}
                          </span>
                          <span className="font-serif text-lg md:text-xl leading-snug">
                            {o}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={`/cities/${c.citySlug}`}
                    className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase hover:text-primary transition-colors"
                    data-testid={`case-study-city-link-${c.citySlug}`}
                  >
                    Explore {c.location.split(" · ")[0]} coverage <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities matrix */}
      <section className="border-b border-border bg-[hsl(0_0%_5%)]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ Capability Matrix ]
          </div>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight tracking-tighter mb-16 max-w-3xl">
            Every discipline the sector demands.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {CAPABILITIES.map((cap, i) => (
              <div
                key={cap}
                className="border-r border-b border-border p-6 md:p-8 flex items-start gap-4"
              >
                <span className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground pt-1.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-xl md:text-2xl leading-tight">
                  {cap}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where we operate */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-8">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
                [ Mining Regions ]
              </div>
              <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.95] tracking-tighter">
                Where NB mines. <br /> <i className="text-primary">Where we protect.</i>
              </h2>
            </div>
            <div className="md:col-span-4">
              <Link
                to="/services/mining-security"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors"
                data-testid="sector-mining-service-cta"
              >
                Full Service Details <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {["bathurst", "belledune", "sussex", "miramichi", "campbellton", "grand-falls"].map((slug) => {
              const c = CITIES.find((x) => x.slug === slug);
              if (!c) return null;
              return (
                <Link
                  key={slug}
                  to={`/cities/${slug}`}
                  data-testid={`mining-region-${slug}`}
                  className="border-r border-b border-border p-8 hover:bg-[hsl(0_0%_7%)] transition-colors group"
                >
                  <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-3">
                    {c.region}
                  </div>
                  <div className="font-serif text-3xl leading-tight group-hover:text-primary transition-colors">
                    {c.name}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <EditorialMarquee />
      <QuoteForm defaultService={SERVICES.find((s) => s.slug === "mining-security")?.name} />
    </div>
  );
}

function Chapter({ label, body }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
        [ {label} ]
      </div>
      <p className="text-base md:text-lg leading-relaxed text-foreground/85 max-w-2xl">
        {body}
      </p>
    </div>
  );
}
